import styled from '@emotion/styled';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import useGlobalStore from '../../../store/useGlobalStore';
import { toast } from 'sonner';
import CommonDialog from '../../../common/CommonDialog';
import { coreSettingsKeys, useGetCustomerNotificationTemplates, useUpdateCustomerNotificationTemplates } from '../../../services/core/settings/settings.hooks';
import MessageTemplateEditor from './MessageTemplateEditor';
import { getGSM7Length, getMissingRequiredVariables, getMissingVariablesErrorMessage, hasInvalidGSM7Characters } from '../../../utility/gsm7Validator';

const StyledSmallToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
    borderRadius: '100px',
    padding: '4px',
    background: '#FFF',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    height: '40px',
    boxSizing: 'border-box',
    border: 'none',
    boxShadow: 'none',

    '& .MuiToggleButtonGroup-grouped': {
        margin: '0 !important',
        border: 'none !important',
        '&:not(:first-of-type)': {
            borderRadius: '50px',
            borderLeft: 'none',
        },
        '&:first-of-type': {
            borderRadius: '50px',
        },
    },

    '& .MuiToggleButton-root': {
        border: 'none',
        borderRadius: '50px !important',
        padding: '6px 14px',
        fontFamily: 'Roboto',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '130%',
        letterSpacing: '-0.48px',
        fontFeatureSettings: "'liga' off, 'clig' off",
        textTransform: 'capitalize',
        height: '32px',
        boxSizing: 'border-box',

        '&.Mui-selected': {
            backgroundColor: '#007AFF',
            color: 'rgba(255, 255, 255, 0.87)',
            border: '1px solid #0066E1 !important',
            boxShadow: '0px 2px 2px 0px rgba(0, 122, 255, 0.25)',
        },

        '&:not(.Mui-selected)': {
            backgroundColor: '#FFF',
            color: 'rgba(0, 0, 0, 0.87)',
            border: 'none !important',
        },

        '&:not(.Mui-selected):hover': {
            backgroundColor: '#fff',
        },

        '&.Mui-selected:hover': {
            backgroundColor: '#007AFF',
        },
    },
}));

// Exact Double Arrow SVGs provided by user
const DoubleUpArrowIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M17 11L12 6L7 11" stroke="#006CDD" strokeWidth="1.5" strokeLinecap="square" />
        <path d="M17 18L12 13L7 18" stroke="#006CDD" strokeWidth="1.5" strokeLinecap="square" />
    </svg>
);

const DoubleDownArrowIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M17.9981 12.5L11.9981 18.5L5.99805 12.5" stroke="#006CDD" strokeWidth="1.5" />
        <path d="M17.9981 5.49902L11.9981 11.499L5.99805 5.49907" stroke="#006CDD" strokeWidth="1.5" />
    </svg>
);

const ResetAllArrowIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" {...props}>
        <path
            d="M12 4V1L8 5L12 9V6C15.31 6 18 8.69 18 12C18 15.31 15.31 18 12 18C8.69 18 6 15.31 6 12H4C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20 12C20 7.58 16.42 4 12 4Z"
            fill="currentColor"
        />
    </svg>
);

// Exact SVG Icons for Invoice & Estimate templates provided by user
const EstimateSentIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
        <path
            d="M15 9.1665V10.4165M15 10.4165C13.7343 10.4165 12.7083 11.1903 12.7083 12.1449C12.7083 13.0995 13.3333 13.6264 15 13.6264C16.6667 13.6264 17.5 14.1203 17.5 15.3548C17.5 16.5894 16.3807 17.0832 15 17.0832M15 10.4165C16.2657 10.4165 17.2917 11.1903 17.2917 12.1449M15 17.0832V18.3332M15 17.0832C13.6192 17.0832 12.5 16.3094 12.5 15.3548"
            stroke="#006CDD"
            strokeWidth="1.25"
            strokeLinejoin="round"
        />
        <path d="M8.33333 1.6665V7.49984H2.5" stroke="#006CDD" strokeWidth="1.25" />
        <path
            d="M16.6397 7.49339V1.67825C16.6397 1.67365 16.6361 1.66992 16.6314 1.66992L8.29712 1.67016L2.5 7.49044V18.325C2.5 18.3296 2.50373 18.3333 2.50833 18.3333H10.817"
            stroke="#006CDD"
            strokeWidth="1.25"
        />
    </svg>
);

const InvoiceSentIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
        <path
            d="M16.6663 17.9165V3.33317C16.6663 2.4127 15.9202 1.6665 14.9997 1.6665H4.99967C4.0792 1.6665 3.33301 2.4127 3.33301 3.33317V17.9165L6.66634 16.2498L9.99967 18.3332L13.333 16.2498L16.6663 17.9165Z"
            stroke="#006CDD"
            strokeWidth="1.25"
            strokeLinejoin="round"
        />
        <path
            d="M10 6.25H11.25C11.9403 6.25 12.5 6.80964 12.5 7.5M10 6.25H8.75C8.05964 6.25 7.5 6.80964 7.5 7.5V7.91667C7.5 8.607 8.05964 9.16667 8.75 9.16667H11.25C11.9403 9.16667 12.5 9.72633 12.5 10.4167V10.8333C12.5 11.5237 11.9403 12.0833 11.25 12.0833H10M10 6.25V5M10 12.0833H8.75C8.05964 12.0833 7.5 11.5237 7.5 10.8333M10 12.0833V13.3333"
            stroke="#006CDD"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const WorkOrderRequestIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
        <path d="M5 10.8335H10" stroke="#006CDD" strokeWidth="1.25" />
        <path d="M5 14.1665H13.3333" stroke="#006CDD" strokeWidth="1.25" />
        <path
            d="M8.75033 1.6665H1.67533C1.67073 1.6665 1.66699 1.67024 1.66699 1.67484V18.3248C1.66699 18.3294 1.67073 18.3332 1.67533 18.3332H16.6587C16.6632 18.3332 16.667 18.3294 16.667 18.3248V11.9728"
            stroke="#006CDD"
            strokeWidth="1.25"
        />
        <path
            d="M12.2917 5.83317L13.5417 7.08317L16.0417 4.58317M18.3333 5.83317C18.3333 8.13435 16.4678 9.99984 14.1667 9.99984C11.8655 9.99984 10 8.13435 10 5.83317C10 3.53199 11.8655 1.6665 14.1667 1.6665C16.4678 1.6665 18.3333 3.53199 18.3333 5.83317Z"
            stroke="#006CDD"
            strokeWidth="1.25"
        />
    </svg>
);

const PlusIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" {...props}>
        <path d="M7 2.9165V11.0832" stroke="#006CDD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2.91699 7H11.0837" stroke="#006CDD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const getSampleValues = (item, previewValuesFromApi = {}) => {
    const defaultAppLink = previewValuesFromApi?.app_link || 'https://apexautoapp.app.link/oHJ1lWxYqVb';
    return {
        customer_name: 'Jhon',
        shop_name: 'Vancouver Mall',
        shop_address: '8700 Northeast Vancouver Mall Drive, Vancouver, WA',
        appointment_date: 'Sep 24, 2026',
        appointment_time: '10:30 AM',
        appointment_day: 'Wednesday',
        vehicle: '2019 Honda Civic Touring',
        estimate_link: 'https://apexautoapp.app.link/7Kq2mXbR9Ld',
        invoice_link: 'https://apexautoapp.app.link/8Lr3nYcS0Me',
        authorization_link: 'https://apexautoapp.app.link/9Ms4oZdT1Nf',
        link: 'https://search.google.com/local/writereview?placeid=ChIJ0c6lfemvlVQRwAC16NXufZs',
        Link: 'https://search.google.com/local/writereview?placeid=ChIJ0c6lfemvlVQRwAC16NXufZs',
        calendar_link: 'https://apex-api.devstree.in/c/K7mQx2Bv9pT',
        ...previewValuesFromApi,
        'app + link': defaultAppLink,
        app_link: defaultAppLink,
        apex_auto_link: defaultAppLink,
    };
};

const renderPreviewWithLinks = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, index) => {
        if (urlRegex.test(part)) {
            return (
                <Typography
                    key={index}
                    component="a"
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        color: '#006CDD',
                        textDecoration: 'underline',
                        wordBreak: 'break-all',
                        fontSize: '14px',
                        lineHeight: '20px',
                    }}
                >
                    {part}
                </Typography>
            );
        }
        return part;
    });
};

const getTemplateHeader = (type) => {
    switch (type) {
        case 'estimateSent':
        case 'estimate':
            return { title: 'Estimate Sent', subtitle: '', Icon: EstimateSentIcon };
        case 'invoiceSent':
        case 'invoice':
            return { title: 'Invoice Sent', subtitle: '', Icon: InvoiceSentIcon };
        case 'workOrderAuthorizationRequest':
        case 'work_order_request':
        case 'workOrderRequest':
            return { title: 'Work Order Request', subtitle: '', Icon: WorkOrderRequestIcon };
        default:
            return { title: type, subtitle: '', Icon: EstimateSentIcon };
    }
};

const extractInvEstList = (invEstObj) => {
    if (!invEstObj) return [];
    let list = [];
    ['estimateSent', 'estimate', 'invoiceSent', 'invoice', 'workOrderAuthorizationRequest', 'work_order_request', 'workOrderRequest'].forEach((key) => {
        const item = invEstObj[key];
        if (Array.isArray(item)) {
            list = list.concat(item);
        } else if (item && typeof item === 'object') {
            list.push(item);
        }
    });
    return list;
};

const InvoiceEstimateTextMessages = () => {
    const queryClient = useQueryClient();
    const { data: notificationTemplatesData, isLoading: isTemplatesLoading } = useGetCustomerNotificationTemplates();
    const updateNotificationTemplatesMutation = useUpdateCustomerNotificationTemplates();

    const { setSettingsDirty, resetSettingsDirty } = useGlobalStore();

    const editorRefs = useRef({});

    const [isResetAllModalOpen, setIsResetAllModalOpen] = useState(false);
    const [isAllCollapsed, setIsAllCollapsed] = useState(true);
    const [expandedItems, setExpandedItems] = useState({});
    const [messageTemplates, setMessageTemplates] = useState({});
    const [enabledStates, setEnabledStates] = useState({});

    const invEstObj = notificationTemplatesData?.templates?.inv_est_texts;
    const invEstList = extractInvEstList(invEstObj);

    // Hydrate state from customer notification templates API response
    useEffect(() => {
        if (notificationTemplatesData) {
            const list = extractInvEstList(notificationTemplatesData?.templates?.inv_est_texts);
            const msgMap = {};
            const enMap = {};
            list.forEach((item) => {
                msgMap[item.type] = item.messageTemplate || '';
                enMap[item.type] = item.isEnabled ?? true;
            });
            setExpandedItems((prev) => {
                const nextExpMap = { ...prev };
                list.forEach((item) => {
                    if (nextExpMap[item.type] === undefined) {
                        nextExpMap[item.type] = false;
                    }
                });
                return nextExpMap;
            });
            setMessageTemplates(msgMap);
            setEnabledStates(enMap);
        }
    }, [notificationTemplatesData]);

    // Derived: original values from API (stable references for dirty comparison)
    const originalMsgMap = useMemo(() => {
        const map = {};
        const list = extractInvEstList(notificationTemplatesData?.templates?.inv_est_texts);
        list.forEach((item) => {
            map[item.type] = item.messageTemplate || '';
        });
        return map;
    }, [notificationTemplatesData]);

    const originalEnMap = useMemo(() => {
        const map = {};
        const list = extractInvEstList(notificationTemplatesData?.templates?.inv_est_texts);
        list.forEach((item) => {
            map[item.type] = item.isEnabled ?? true;
        });
        return map;
    }, [notificationTemplatesData]);

    // Sync dirty state to global store whenever local state diverges from API data
    useEffect(() => {
        if (!notificationTemplatesData) return;
        const list = extractInvEstList(notificationTemplatesData?.templates?.inv_est_texts);
        const msgChanged = list.some((item) => messageTemplates[item.type] !== originalMsgMap[item.type]);
        const enChanged = list.some((item) => enabledStates[item.type] !== originalEnMap[item.type]);
        setSettingsDirty(msgChanged || enChanged);
    }, [messageTemplates, enabledStates, originalMsgMap, originalEnMap, notificationTemplatesData, setSettingsDirty]);

    // Clear dirty flag when component unmounts
    useEffect(() => {
        return () => resetSettingsDirty();
    }, [resetSettingsDirty]);

    const toggleAllCollapse = () => {
        const nextState = !isAllCollapsed;
        setIsAllCollapsed(nextState);
        const updated = {};
        invEstList.forEach((item) => {
            updated[item.type] = !nextState;
        });
        setExpandedItems(updated);
    };

    const toggleItemCollapse = (type) => {
        setExpandedItems((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    };

    const handleEnableToggle = (type, val) => {
        if (val !== null) {
            const isEnable = val === 'enable';
            setEnabledStates((prev) => ({
                ...prev,
                [type]: isEnable,
            }));
            setExpandedItems((prev) => ({
                ...prev,
                [type]: isEnable,
            }));
        }
    };

    const handleMessageChange = (type, val) => {
        setMessageTemplates((prev) => ({
            ...prev,
            [type]: val,
        }));
    };

    const handleResetTemplate = (type, defaultMsg) => {
        setMessageTemplates((prev) => ({
            ...prev,
            [type]: defaultMsg,
        }));
        toast.success('Message reset to default');
    };

    const isResetAllDisabled = invEstList.every((item) => {
        const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
        const isEnabled = enabledStates[item.type] !== false;
        const isDefaultMsg = currentMsg === item.defaultMessageTemplate;
        return isDefaultMsg && isEnabled;
    });

    const handleResetAll = async () => {
        const updatedMsgMap = {};
        const updatedEnMap = {};

        invEstList.forEach((item) => {
            updatedMsgMap[item.type] = item.defaultMessageTemplate;
            updatedEnMap[item.type] = true;
        });

        setMessageTemplates(updatedMsgMap);
        setEnabledStates(updatedEnMap);

        try {
            const rawInvEst = notificationTemplatesData?.templates?.inv_est_texts || {};
            const updatedInvEst = { ...rawInvEst };

            ['estimateSent', 'estimate', 'invoiceSent', 'invoice', 'workOrderAuthorizationRequest', 'work_order_request', 'workOrderRequest'].forEach((key) => {
                if (!rawInvEst[key]) return;

                const currentItems = Array.isArray(rawInvEst[key]) ? rawInvEst[key] : [rawInvEst[key]];
                const updatedItems = currentItems.map((item) => {
                    const defaultMsg = item.defaultMessageTemplate;
                    const matchedVars = defaultMsg.match(/\{([\w_]+)\}/g) || [];
                    const uniqueVars = [];
                    matchedVars.forEach((v) => {
                        const varName = v.replace(/[\{\}]/g, '');
                        if (!uniqueVars.includes(varName)) {
                            uniqueVars.push(varName);
                        }
                    });

                    const variablesPayload = uniqueVars.map((varName, idx) => ({
                        varName,
                        varOrder: idx + 1,
                        isRequired: item.requiredVariables?.includes(varName) || false,
                    }));

                    return {
                        ...item,
                        isEnabled: true,
                        messageTemplate: defaultMsg,
                        isCustomized: false,
                        variables: variablesPayload,
                    };
                });

                if (Array.isArray(rawInvEst[key])) {
                    updatedInvEst[key] = updatedItems;
                } else {
                    updatedInvEst[key] = updatedItems[0];
                }
            });

            const payload = {
                ...notificationTemplatesData,
                templates: {
                    ...notificationTemplatesData?.templates,
                    inv_est_texts: updatedInvEst,
                },
            };

            const response = await updateNotificationTemplatesMutation.mutateAsync(payload);
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.customerNotificationTemplates() });
            queryClient.invalidateQueries({ queryKey: coreSettingsKeys.config() });
            toast.success(response?.message || 'All templates reset to default');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to reset templates');
        }
    };

    const handleConfirmResetAll = async () => {
        setIsResetAllModalOpen(false);
        await handleResetAll();
    };

    // Inserting variable chip at the exact caret/cursor position inside the editor
    const handleVariableChipClick = (type, varName) => {
        const editor = editorRefs.current[type];
        if (editor?.insertVariable) {
            editor.insertVariable(varName);
        } else {
            const currentMsg = messageTemplates[type] || '';
            const varToken = `{${varName}}`;
            setMessageTemplates((prev) => ({
                ...prev,
                [type]: currentMsg ? `${currentMsg} ${varToken}` : varToken,
            }));
        }
    };

    const generatePreviewText = (templateStr, item) => {
        if (templateStr === item?.messageTemplate && item?.preview?.text) {
            return item.preview.text;
        }

        let result = templateStr || '';
        const samples = getSampleValues(item, notificationTemplatesData?.previewValues);

        Object.keys(samples).forEach((key) => {
            const regex = new RegExp(`\\{${key}\\}`, 'g');
            result = result.replace(regex, samples[key]);
        });
        return result;
    };

    const doCancel = () => {
        if (notificationTemplatesData) {
            const list = extractInvEstList(notificationTemplatesData?.templates?.inv_est_texts);
            const msgMap = {};
            const enMap = {};
            list.forEach((item) => {
                msgMap[item.type] = item.messageTemplate || '';
                enMap[item.type] = item.isEnabled ?? true;
            });
            setMessageTemplates(msgMap);
            setEnabledStates(enMap);
        }
        resetSettingsDirty();
    };

    const handleCancel = () => {
        doCancel();
    };

    const handleSave = async () => {
        const itemsWithMissingVars = invEstList.filter((item) => {
            const isEnabled = enabledStates[item.type] !== false;
            const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
            return isEnabled && getMissingRequiredVariables(item, currentMsg).length > 0;
        });

        const itemsWithExceededLimit = invEstList.filter((item) => {
            const isEnabled = enabledStates[item.type] !== false;
            const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
            const previewText = generatePreviewText(currentMsg, item);
            return isEnabled && previewText && getGSM7Length(previewText) > 280;
        });

        const itemsWithInvalidGSM7 = invEstList.filter((item) => {
            const isEnabled = enabledStates[item.type] !== false;
            const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
            return isEnabled && hasInvalidGSM7Characters(currentMsg);
        });

        const allInvalidItems = [...itemsWithMissingVars, ...itemsWithExceededLimit, ...itemsWithInvalidGSM7].filter(
            (item, index, self) => self.findIndex((t) => t.type === item.type) === index
        );

        if (allInvalidItems.length > 0) {
            setExpandedItems((prev) => {
                const next = { ...prev };
                allInvalidItems.forEach((item) => {
                    next[item.type] = true;
                });
                return next;
            });

            if (itemsWithMissingVars.length > 0) {
                const totalMissingVars = itemsWithMissingVars.reduce((sum, item) => {
                    const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
                    return sum + getMissingRequiredVariables(item, currentMsg).length;
                }, 0);

                if (itemsWithMissingVars.length === 1 && totalMissingVars === 1) {
                    const item = itemsWithMissingVars[0];
                    const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
                    const missingVars = getMissingRequiredVariables(item, currentMsg);
                    const cleanVar = missingVars[0]
                        .replace(/[\{\}]/g, '')
                        .replace(/_/g, ' ')
                        .trim();
                    const varName = cleanVar ? cleanVar.charAt(0).toUpperCase() + cleanVar.slice(1) : missingVars[0];
                    const headerInfo = getTemplateHeader(item.type);
                    const title = item.title || headerInfo?.title || item.type;
                    toast.error(`“${varName}” is missing from “${title}.” Add it before saving`);
                } else {
                    toast.error('Some messages are missing required details. Review the highlighted messages before saving');
                }
            } else if (itemsWithExceededLimit.length > 0) {
                toast.error('Some messages exceed the 280-character limit. Shorten them before saving');
            } else if (itemsWithInvalidGSM7.length > 0) {
                toast.error('Message contains invalid characters. Please remove and try again.');
            }
            return;
        }

        try {
            const rawInvEst = notificationTemplatesData?.templates?.inv_est_texts || {};
            const updatedInvEst = { ...rawInvEst };

            ['estimateSent', 'estimate', 'invoiceSent', 'invoice', 'workOrderAuthorizationRequest', 'work_order_request', 'workOrderRequest'].forEach((key) => {
                if (!rawInvEst[key]) return;

                const currentItems = Array.isArray(rawInvEst[key]) ? rawInvEst[key] : [rawInvEst[key]];
                const updatedItems = currentItems.map((item) => {
                    const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
                    const isEnabled = enabledStates[item.type] ?? item.isEnabled;

                    const matchedVars = currentMsg.match(/\{([\w_]+)\}/g) || [];
                    const uniqueVars = [];
                    matchedVars.forEach((v) => {
                        const varName = v.replace(/[\{\}]/g, '');
                        if (!uniqueVars.includes(varName)) {
                            uniqueVars.push(varName);
                        }
                    });

                    const variablesPayload = uniqueVars.map((varName, idx) => ({
                        varName,
                        varOrder: idx + 1,
                        isRequired: item.requiredVariables?.includes(varName) || false,
                    }));

                    return {
                        ...item,
                        isEnabled,
                        messageTemplate: currentMsg,
                        isCustomized: currentMsg !== item.defaultMessageTemplate,
                        variables: variablesPayload,
                    };
                });

                if (Array.isArray(rawInvEst[key])) {
                    updatedInvEst[key] = updatedItems;
                } else {
                    updatedInvEst[key] = updatedItems[0];
                }
            });

            const payload = {
                ...notificationTemplatesData,
                templates: {
                    ...notificationTemplatesData?.templates,
                    inv_est_texts: updatedInvEst,
                },
            };

            const response = await updateNotificationTemplatesMutation.mutateAsync(payload);
            resetSettingsDirty();
            toast.success(response?.message || 'Changes Saved');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to save changes');
        }
    };

    return (
        <Box
            sx={{
                display: 'none',
            }}
        >
            <Grid
                container
                sx={{
                    boxShadow: 3,
                    bgcolor: 'white',
                    padding: '24px',
                    borderRadius: '10px',
                    marginTop: '32px',
                    justifyContent: 'space-between',
                }}
            >
                {/* Left Column: Title & Subtitle */}
                <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px', color: '#1A1A1A' }}>
                        Invoice &amp; Estimate Messages
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            mt: '12px',
                            fontSize: '16px',
                            color: '#000000',
                            lineHeight: '24px',
                            letterSpacing: '0.15px',
                            maxWidth: 360,
                        }}
                    >
                        Customize SMS messages sent to customers.
                    </Typography>

                    <Divider sx={{ my: '12px', borderColor: 'rgba(0, 0, 0, 0.10)', maxWidth: 360 }} />

                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '16px',
                            color: '#000000',
                            lineHeight: '24px',
                            letterSpacing: '0.15px',
                            maxWidth: 360,
                        }}
                    >
                        Click a variable to insert it — it will be replaced with real details when the message is sent.
                    </Typography>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} sm={8} md={8} lg={8} xl={8}>
                    {/* Main Collapse All Toggle & Reset All Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '16px' }}>
                        <Box
                            onClick={() => {
                                if (!isResetAllDisabled) {
                                    setIsResetAllModalOpen(true);
                                }
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#D32F2F',
                                cursor: isResetAllDisabled ? 'default' : 'pointer',
                                opacity: isResetAllDisabled ? 0.4 : 1,
                                fontSize: '15px',
                                fontWeight: 500,
                                lineHeight: '130%',
                                letterSpacing: '0.46px',
                                textTransform: 'uppercase',
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                    d="M12.5 7.5H17.5V2.5M17.0732 12.5C16.0437 15.413 13.2656 17.5 10 17.5C5.85786 17.5 2.5 14.1422 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C13.1077 2.5 15.7738 4.39005 16.9117 7.08333"
                                    stroke="#D32F2F"
                                    strokeWidth="1.5"
                                    strokeLinecap="square"
                                />
                            </svg>
                            <span>RESET ALL TO DEFAULT</span>
                        </Box>

                        <Box
                            onClick={toggleAllCollapse}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                color: '#006CDD',
                                cursor: 'pointer',
                                userSelect: 'none',
                                fontFeatureSettings: "'liga' off, 'clig' off",
                                fontFamily: 'Roboto',
                                fontSize: '15px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: '26px',
                                letterSpacing: '0.46px',
                                textTransform: 'uppercase',
                                '&:hover': { opacity: 0.85 },
                            }}
                        >
                            {isAllCollapsed ? <DoubleDownArrowIcon /> : <DoubleUpArrowIcon />}
                            <span>{isAllCollapsed ? 'EXPAND ALL' : 'COLLAPSE ALL'}</span>
                        </Box>
                    </Box>

                    {/* List of Invoice & Estimate Templates */}
                    <Stack spacing={2}>
                        {invEstList.map((item) => {
                            const headerInfo = getTemplateHeader(item.type);
                            const IconComponent = headerInfo.Icon;
                            const isItemExpanded = expandedItems[item.type] === true;
                            const isEnabled = enabledStates[item.type] !== false;
                            const currentMsg = messageTemplates[item.type] ?? item.messageTemplate;
                            const previewText = generatePreviewText(currentMsg, item);

                            // Filter allowedVariables: only show variables NOT currently present in currentMsg
                            const unaddedVariables = (item.allowedVariables || []).filter((v) => !currentMsg.toLowerCase().includes(`{${v.toLowerCase()}}`));

                            const requiredVarNames = (
                                item.requiredVariables ||
                                (item.variables || []).filter((v) => v.isRequired).map((v) => v.varName) ||
                                []
                            ).map((v) => v.replace(/[{}]/g, '').toLowerCase());

                            const unaddedRequired = unaddedVariables.filter((v) => requiredVarNames.includes(v.toLowerCase()));
                            const unaddedOptional = unaddedVariables.filter((v) => !requiredVarNames.includes(v.toLowerCase()));

                            const missingReqVars = getMissingRequiredVariables(item, currentMsg);
                            const hasMissingReqVars = missingReqVars.length > 0;
                            const hasInvalidChars = hasInvalidGSM7Characters(currentMsg);
                            const previewLength = getGSM7Length(previewText);
                            const isCharOverflow = previewLength > 280;
                            const isResetDisabled = currentMsg === item.defaultMessageTemplate;

                            return (
                                <Box
                                    key={item.type}
                                    sx={{
                                        borderRadius: '8px',
                                        border: '1px solid rgba(0, 0, 0, 0.10)',
                                        bgcolor: '#FFF',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {/* Template Card Header */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            bgcolor: '#F8F8F8',
                                            p: '16px',
                                        }}
                                        onClick={() => toggleItemCollapse(item.type)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <IconComponent style={{ width: 20, height: 20 }} />
                                            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A' }}>
                                                {headerInfo.title}{' '}
                                                {headerInfo.subtitle && (
                                                    <Typography component="span" sx={{ fontWeight: 400, color: '#666666' }}>
                                                        {headerInfo.subtitle}
                                                    </Typography>
                                                )}
                                            </Typography>
                                        </Box>

                                        <Stack direction="row" spacing={1.5} alignItems="center" onClick={(e) => e.stopPropagation()}>
                                            <StyledSmallToggleButtonGroup
                                                size="small"
                                                value={isEnabled ? 'enable' : 'disable'}
                                                exclusive
                                                onChange={(e, val) => handleEnableToggle(item.type, val)}
                                            >
                                                <ToggleButton value="enable">{isEnabled ? 'Enabled' : 'Enable'}</ToggleButton>
                                                <ToggleButton value="disable">{isEnabled ? 'Disable' : 'Disabled'}</ToggleButton>
                                            </StyledSmallToggleButtonGroup>

                                            <IconButton size="small" onClick={() => toggleItemCollapse(item.type)}>
                                                {isItemExpanded ? (
                                                    <KeyboardArrowUpIcon sx={{ color: '#666' }} />
                                                ) : (
                                                    <KeyboardArrowDownIcon sx={{ color: '#666' }} />
                                                )}
                                            </IconButton>
                                        </Stack>
                                    </Box>

                                    {/* Template Card Collapsible Body */}
                                    <Collapse in={isItemExpanded}>
                                        <Box
                                            sx={{
                                                p: '16px',
                                                bgcolor: '#FFF',
                                                opacity: isEnabled ? 1 : 0.4,
                                                pointerEvents: isEnabled ? 'auto' : 'none',
                                                userSelect: isEnabled ? 'auto' : 'none',
                                            }}
                                        >
                                            {/* Allowed Variables Section (Required & Optional Rows) */}
                                            {unaddedVariables.length > 0 && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mb: '16px' }}>
                                                    {/* Required Variables Row */}
                                                    {unaddedRequired.length > 0 && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            <Typography
                                                                sx={{
                                                                    color: '#757575',
                                                                    fontSize: '14px',
                                                                    fontWeight: 400,
                                                                    lineHeight: '130%',
                                                                }}
                                                            >
                                                                Required
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                                                {unaddedRequired.map((v) => (
                                                                    <Box
                                                                        key={v}
                                                                        onClick={() => handleVariableChipClick(item.type, v)}
                                                                        sx={{
                                                                            borderRadius: '6px',
                                                                            bgcolor: 'rgba(0, 108, 221, 0.08)',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            padding: '4px 6px',
                                                                            gap: '4px',
                                                                            cursor: 'pointer',
                                                                            userSelect: 'none',
                                                                            '&:hover': {
                                                                                bgcolor: 'rgba(0, 108, 221, 0.14)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <PlusIcon />
                                                                        <Typography
                                                                            sx={{
                                                                                color: '#006CDD',
                                                                                fontSize: '12px',
                                                                                fontWeight: 400,
                                                                                lineHeight: '130%',
                                                                            }}
                                                                        >
                                                                            {v}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* Optional Variables Row */}
                                                    {unaddedOptional.length > 0 && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            <Typography
                                                                sx={{
                                                                    color: '#757575',
                                                                    fontSize: '14px',
                                                                    fontWeight: 400,
                                                                    lineHeight: '130%',
                                                                }}
                                                            >
                                                                Optional
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                                                {unaddedOptional.map((v) => (
                                                                    <Box
                                                                        key={v}
                                                                        onClick={() => handleVariableChipClick(item.type, v)}
                                                                        sx={{
                                                                            borderRadius: '6px',
                                                                            bgcolor: 'rgba(0, 108, 221, 0.08)',
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            padding: '4px 6px',
                                                                            gap: '4px',
                                                                            cursor: 'pointer',
                                                                            userSelect: 'none',
                                                                            '&:hover': {
                                                                                bgcolor: 'rgba(0, 108, 221, 0.14)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <PlusIcon />
                                                                        <Typography
                                                                            sx={{
                                                                                color: '#006CDD',
                                                                                fontSize: '12px',
                                                                                fontWeight: 400,
                                                                                lineHeight: '130%',
                                                                            }}
                                                                        >
                                                                            {v}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}

                                            {/* Message and Preview Side-by-Side */}
                                            <Grid container spacing={2.5}>
                                                {/* Left: Message Editor Box */}
                                                <Grid item xs={12} md={6}>
                                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: '8px', color: '#1A1A1A' }}>Message</Typography>
                                                    <Box
                                                        sx={{
                                                            borderRadius: '8px',
                                                            border:
                                                                hasInvalidChars || hasMissingReqVars ? '1px solid #D32F2F' : '1px solid rgba(0, 0, 0, 0.10)',
                                                            bgcolor: '#FFF',
                                                            boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                                            p: '12px 16px',
                                                            height: '320px',
                                                            overflowY: 'auto',
                                                        }}
                                                    >
                                                        <MessageTemplateEditor
                                                            ref={(el) => {
                                                                editorRefs.current[item.type] = el;
                                                            }}
                                                            value={currentMsg}
                                                            onChange={(val) => handleMessageChange(item.type, val)}
                                                            placeholder="Edit message..."
                                                        />
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: '8px' }}>
                                                        <Typography
                                                            component="span"
                                                            onClick={() => {
                                                                if (!isResetDisabled) {
                                                                    handleResetTemplate(item.type, item.defaultMessageTemplate);
                                                                }
                                                            }}
                                                            sx={{
                                                                color: '#D32F2F',
                                                                fontSize: '15px',
                                                                fontWeight: 500,
                                                                lineHeight: '130%',
                                                                letterSpacing: '0.46px',
                                                                textTransform: 'uppercase',
                                                                cursor: isResetDisabled ? 'default' : 'pointer',
                                                                opacity: isResetDisabled ? 0.4 : 1,
                                                            }}
                                                        >
                                                            RESET TO DEFAULT
                                                        </Typography>
                                                    </Box>
                                                    {hasInvalidChars && (
                                                        <Typography
                                                            sx={{
                                                                color: '#D32F2F',
                                                                fontSize: '14px',
                                                                fontWeight: 400,
                                                                lineHeight: '130%',
                                                                mt: '12px',
                                                            }}
                                                        >
                                                            Message contains invalid characters. Please remove and try again.
                                                        </Typography>
                                                    )}
                                                    {!hasInvalidChars && hasMissingReqVars && (
                                                        <Typography
                                                            sx={{
                                                                color: '#D32F2F',
                                                                fontSize: '14px',
                                                                fontWeight: 400,
                                                                lineHeight: '130%',
                                                                mt: '12px',
                                                            }}
                                                        >
                                                            {getMissingVariablesErrorMessage(missingReqVars)}
                                                        </Typography>
                                                    )}
                                                </Grid>

                                                {/* Right: Preview Box */}
                                                <Grid item xs={12} md={6}>
                                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: '8px', color: '#1A1A1A' }}>Preview</Typography>
                                                    <Box
                                                        sx={{
                                                            borderRadius: '8px',
                                                            border: isCharOverflow ? '1px solid #D32F2F' : '1px solid rgba(0, 0, 0, 0.10)',
                                                            bgcolor: '#FFF',
                                                            boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                                            p: '12px 16px',
                                                            height: '320px',
                                                            overflowY: 'auto',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                borderRadius: '12px',
                                                                bgcolor: '#F0F0F0',
                                                                maxWidth: '260px',
                                                                p: '14px 12px',
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '14px',
                                                                    color: '#374151',
                                                                    lineHeight: '20px',
                                                                    whiteSpace: 'pre-line',
                                                                    wordBreak: 'break-word',
                                                                }}
                                                            >
                                                                {renderPreviewWithLinks(previewText)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mt: '8px' }}>
                                                        <Typography
                                                            sx={{ fontSize: '14px', lineHeight: '130%', color: isCharOverflow ? '#D32F2F' : '#9E9E9E' }}
                                                        >
                                                            {previewLength} / 280 characters
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Stack>

                    {/* Bottom Right Actions */}
                    <Grid container justifyContent="flex-end" spacing={2} sx={{ marginTop: '24px' }}>
                        <Grid item>
                            <Stack spacing={2} direction="row">
                                <Button variant="outlined" color="primary" onClick={handleCancel}>
                                    CANCEL
                                </Button>
                                <Button variant="contained" color="primary" onClick={handleSave} disabled={updateNotificationTemplatesMutation.isPending}>
                                    SAVE
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Confirmation Modal for Resetting All Messages to Default */}
            <CommonDialog
                open={isResetAllModalOpen}
                onClose={() => setIsResetAllModalOpen(false)}
                onSave={handleConfirmResetAll}
                loader={updateNotificationTemplatesMutation.isPending}
                dialogTitle="⚠️ Reset all messages to default?"
                dialogSubtitle="Resetting will replace all customized invoice and estimate text messages with the default templates."
                dialogSubtitle2="Your changes will be lost."
                saveButtonText="RESET ALL"
                saveButtonType="error"
                saveButtonWidth="126px"
                cancelButtonText="CANCEL"
            />

            {/* Dialog Loader during initial loading or updating */}
            <Dialog fullScreen={false} open={isTemplatesLoading || updateNotificationTemplatesMutation.isPending}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default InvoiceEstimateTextMessages;
